/**
 * The "CRM Partner" block on an employee record, logic only (both looks).
 *
 * Go-live 1 November checklist: "option in HR to Create CRM Partner using
 * existing employee information... tickbox with data summary and button
 * 'Create Partner' ... CRM Profile Status in Employee Record - Show 'CRM
 * profile existent'. - Add direct link to CRM profile."
 *
 * States the markup draws:
 *  - loading                 fetching the link or the summary
 *  - profile                 linked: "CRM profile existent" + link
 *  - summary                 not linked: what Create Partner would make,
 *                            with the tick box (confirmed) gating the button
 *  - conflict                the 409: a partner with the same name exists;
 *                            "Link to this partner" links it instead
 *  - error                   the server's own sentence, shown as it is
 *
 * `initialProfile` is the link the admin list row already carries; when it
 * is there nothing is fetched (the summary is only needed while unlinked).
 */
import { useCallback, useEffect, useState } from 'react';
import employeeCrmPartnerService from '../../services/employeeCrmPartnerService';
import { extractApiError, getErrorMessage } from '../../utils/errorMessages';

export const crmProfilePath = (profileId) => `/dashboard/crm/profile/${profileId}`;

const APPROVAL_LABELS = { pending: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };

/** "Pending Approval" etc. (the list row carries only the code). */
export const approvalLabel = (profile) => (
  profile?.approval_status_label || APPROVAL_LABELS[profile?.approval_status] || ''
);

/** 17 May 1990, or null. */
export const formatHrDate = (iso) => (
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null
);

/** The data summary as label/value rows, in the order both looks show them. */
export function summaryRows(summary) {
  const { contact } = summary;
  return [
    { label: 'Partner name', value: summary.legal_entity_name },
    { label: 'Partner type', value: summary.partner_type },
    { label: 'Country', value: summary.country || 'Not set (add it in CRM)' },
    { label: 'Contact', value: [contact.name, contact.role_title].filter(Boolean).join(', ') },
    { label: 'Email', value: contact.emails.join(', ') || 'Not set' },
    { label: 'Work phone', value: contact.work_phone || 'Not set' },
    { label: 'Personal phone', value: contact.personal_phone || 'Not set' },
    { label: 'Date of birth', value: formatHrDate(contact.date_of_birth) || 'Not set' },
  ];
}

export default function useEmployeeCrmPartner(employeeId, initialProfile = null) {
  const [profile, setProfile] = useState(initialProfile);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!initialProfile);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setProfile(initialProfile);
    setSummary(null);
    setConfirmed(false);
    setConflict(null);
    setError('');
    if (!employeeId || initialProfile) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    employeeCrmPartnerService.get(employeeId)
      .then((data) => {
        if (cancelled) return;
        if (data.linked) setProfile(data.profile);
        else setSummary(data.summary);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, "Couldn't load the CRM partner details. Please try again."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // initialProfile is compared by id: a fresh list row object must not refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, initialProfile?.id]);

  const create = useCallback(async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    setError('');
    setConflict(null);
    try {
      const data = await employeeCrmPartnerService.create(employeeId);
      setProfile(data.profile);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.existing_profile) {
        setConflict(err.response.data.existing_profile);
      }
      // The refusal names the partner, and a name such as "Bloom" trips
      // getErrorMessage's technical-text filter ("oom"); show it as it is.
      setError(extractApiError(err, "Couldn't create the CRM partner. Please try again."));
    } finally {
      setBusy(false);
    }
  }, [busy, confirmed, employeeId]);

  const linkExisting = useCallback(async () => {
    if (!conflict || busy) return;
    setBusy(true);
    setError('');
    try {
      const data = await employeeCrmPartnerService.link(employeeId, conflict.id);
      setProfile(data.profile);
      setConflict(null);
    } catch (err) {
      setError(extractApiError(err, "Couldn't link the CRM partner. Please try again."));
    } finally {
      setBusy(false);
    }
  }, [busy, conflict, employeeId]);

  return {
    loading,
    profile,
    summary,
    confirmed,
    setConfirmed,
    busy,
    conflict,
    error,
    create,
    linkExisting,
  };
}
